/**
 * DraggableDialog - A reusable draggable dialog component
 * 
 * This component provides a draggable dialog with customizable header styles.
 * It supports two variants: 'details' (grey header) and 'help' (purple header).
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Paper
} from '@mui/material';
import { Close } from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';

// Draggable Paper component
function DraggablePaper(props) {
  const nodeRef = useRef(null);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const isDraggingRef = useRef(false);
  const dragStartRef = useRef({ x: 0, y: 0 });

  const handleMouseDown = (e) => {
    // Only allow dragging from the title area
    if (e.target.closest('#draggable-dialog-title')) {
      isDraggingRef.current = true;
      dragStartRef.current = {
        x: e.clientX - position.x,
        y: e.clientY - position.y
      };
      e.preventDefault();
      
      // Add listeners
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }
  };

  const handleMouseMove = (e) => {
    if (isDraggingRef.current) {
      setPosition({
        x: e.clientX - dragStartRef.current.x,
        y: e.clientY - dragStartRef.current.y
      });
    }
  };

  const handleMouseUp = () => {
    isDraggingRef.current = false;
    document.removeEventListener('mousemove', handleMouseMove);
    document.removeEventListener('mouseup', handleMouseUp);
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  return (
    <Paper
      {...props}
      ref={nodeRef}
      onMouseDown={handleMouseDown}
      style={{
        ...props.style,
        transform: `translate(${position.x}px, ${position.y}px)`,
        cursor: 'default'
      }}
    />
  );
}

/**
 * DraggableDialog component
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether the dialog is open
 * @param {Function} props.onClose - Callback when dialog is closed
 * @param {string} props.title - Dialog title text
 * @param {ReactNode} props.children - Dialog content
 * @param {string} props.variant - 'details' (grey header) or 'help' (blue header), default is 'details'
 * @param {Object} props.PaperProps - Additional props for the Paper component
 * @param {Object} props.contentSx - Additional sx props for DialogContent
 * @param {string} props.maxWidth - Max width of dialog (e.g., 'sm', 'md', 'lg', false)
 * @param {boolean} props.fullWidth - Whether to use full width
 * @param {number|string} props.width - Custom width
 * @param {number|string} props.height - Custom height
 * @param {boolean} props.resizable - Whether the dialog is resizable
 */
function DraggableDialog({
  open,
  onClose,
  title,
  children,
  variant = 'details',
  PaperProps = {},
  contentSx = {},
  maxWidth = false,
  fullWidth = false,
  width,
  height = 'auto',
  resizable = false,
  ...otherProps
}) {
  const theme = useTheme();
  const isDetailsVariant = variant === 'details';
  
  // Use proper theme-based header style with consistent height
  // Details dialogs get grey background, help dialogs get purple
  const bgColor = isDetailsVariant ? theme.palette.grey[100] : theme.palette.panel.help;
  const headerSx = {
    padding: theme.spacing(1.5),
    height: theme.dimensions.panel.headerHeight,
    borderBottom: `1px solid ${theme.palette.divider}`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    backgroundColor: bgColor,
    cursor: 'grab'
  };

  // Title styles - using theme values
  const titleSx = {
    fontWeight: theme.typography.fontWeight.semibold,
    fontSize: theme.typography.fontSize.base
  };

  // Use theme-based dialog paper style
  const defaultPaperStyle = {
    pointerEvents: 'auto',
    width: width || 'fit-content',
    minWidth: theme.spacing(50), // 400px at base scale
    maxWidth: maxWidth === false ? '90vw' : theme.spacing(62.5), // 500px at base scale
    height: height || 'auto',
    minHeight: '300px',
    maxHeight: height === 'auto' ? '80vh' : undefined,
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: theme.shadows[21] || '0 8px 32px rgba(0,0,0,0.2)',
    zIndex: 1300,
    resize: resizable ? 'both' : 'none'
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperComponent={DraggablePaper}
      aria-labelledby="draggable-dialog-title"
      maxWidth={maxWidth}
      fullWidth={fullWidth}
      hideBackdrop
      disableEnforceFocus
      disableAutoFocus
      disableRestoreFocus
      style={{ pointerEvents: 'none' }}
      PaperProps={{
        ...PaperProps,
        style: {
          ...defaultPaperStyle,
          ...(PaperProps.style || {})
        }
      }}
      {...otherProps}
    >
      <Box id="draggable-dialog-title" sx={headerSx}>
        <Typography 
          variant={isDetailsVariant ? "body2" : "subtitle1"} 
          sx={titleSx}
        >
          {title}
        </Typography>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{ 
            padding: theme.spacing(0.25)
          }}
        >
          <Close sx={{ fontSize: theme.dimensions.icon.small }} />
        </IconButton>
      </Box>
      <DialogContent sx={{
        p: theme.spacing(2),
        flex: 1,
        overflow: 'auto',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        ...contentSx
      }}>
        {children}
      </DialogContent>
    </Dialog>
  );
}

export { DraggableDialog, DraggablePaper };
export default DraggableDialog;